import without from "lodash/without";

export enum ParsingService {
  DocX4J = "DOCX4J",
}

export enum ConversionService {
  CKEditor = "CKEDITOR",
  DocX4J = "DOCX4J",
}

export enum DocumentStatus {
  Uploaded = "UPLOADED",
  Processing = "PROCESSING",
  Completed = "COMPLETED",
  // Failure exists for participants doc and HAQ spreadsheets
  Failure = "FAILURE",
  // Failed only related to upload
  Failed = "FAILED",
  Draft = "DRAFT",
  Final = "FINAL",
  InVerification = "IN_VERIFICATION",
  // ToDo (AV): status Shared. Back-end doesn't provide such a status to the client side. We have to revisit it.
  Shared = "SHARED",
  Reviewed = "REVIEWED",
  Acknowledged = "ACKNOWLEDGED",
  Cleared = "CLEARED",
  Cancelled = "CANCELLED",
  UploadInitiated = "UPLOAD_INITIATED",
  UploadProcessing = "UPLOAD_PROCESSING",
  UploadFailed = "UPLOAD_FAILED",
  InReview = "IN_REVIEW",
}

export enum DocumentComponentStatus {
  Draft = "DRAFT",
  InReview = "IN_REVIEW",
  Final = "FINAL",
  Cleared = "CLEARED",
}

export enum DocumentViewMode {
  Editing = "Editing",
  Viewing = "Viewing",
  Review = "Review",
}

export enum DocumentSubtype {
  SupportingInformation = "SUPPORTING_INFORMATION",
  Spreadsheet = "SPREADSHEET",
  Question = "QUESTION",
  QuestionResponse = "QUESTION_RESPONSE",
  HADecisionLetter = "HA_DECISION_LETTER",
  AssessmentReport = "ASSESSMENT_REPORT",
  ProjectParticipants = "PROJECT_PARTICIPANTS",
  InspectionReports = "INSPECTION_REPORTS",
  CoverLetter = "COVER_LETTER",
  ApplicantsStrategy = "APPLICANTS_STRATEGY",
  ECTD = "ECTD",
  ReferenceDocument = "REFERENCE_DOCUMENT",
  StructuredDocument = "STRUCTURED_DOCUMENT",
}

export const BASE_DOCUMENT_SUBTYPES = [
  DocumentSubtype.SupportingInformation,
  DocumentSubtype.HADecisionLetter,
  DocumentSubtype.ReferenceDocument,
  DocumentSubtype.AssessmentReport,
  DocumentSubtype.CoverLetter,
  DocumentSubtype.InspectionReports,
  DocumentSubtype.ApplicantsStrategy,
];

export enum DocumentAction {
  Edit = "EDIT",
  EditName = "EDIT_NAME",
  View = "VIEW",
  ViewSections = "VIEW_SECTIONS",
  Submit = "SUBMIT",
  SendCopy = "SEND_COPY",
  Delete = "DELETE",
  Download = "DOWNLOAD",
  ChangeStatus = "CHANGE_STATUS",
  ViewContentInfo = "VIEW_CONTENT_INFO",
  ViewSubmitHistory = "VIEW_SUBMIT_HISTORY",
  VersionHistory = "VERSION_HISTORY",
  HandleClearedStatus = "HANDLE_CLEARED_STATUS",
  AddQuestion = "ADD_QUESTION",
  ReplaceFile = "REPLACE_FILE",
}

export const ENG_LANG_CODE = "eng";

export const CSV_FILE_TYPE = "csv";
export const PDF_FILE_TYPE = "pdf";
export const XML_FILE_TYPE = "xml";
export const XPT_FILE_TYPE = "xpt";
export const TIFF_FILE_TYPE = "tiff";
export const JPG_FILE_TYPE = "jpg";
export const JPEG_FILE_TYPE = "jpeg";
export const PNG_FILE_TYPE = "png";
export const TXT_FILE_TYPE = "txt";
export const MPEG_FILE_TYPE = "mpeg";
export const DOC_FILE_TYPE = "doc";
export const DOCX_FILE_TYPE = "docx";
export const XLSX_FILE_TYPE = "xlsx";
export const XLSM_FILE_TYPE = "xlsm";
export const XLS_FILE_TYPE = "xls";
export const XSl_FILE_TYPE = "xsl";
export const PPT_FILE_TYPE = "ppt";
export const PPTX_FILE_TYPE = "pptx";
export const BMP_FILE_TYPE = "bmp";
export const MP4_FILE_TYPE = "mp4";
export const DTD_FILE_TYPE = "dtd";
export const MOD_FILE_TYPE = "mod";
export const XSD_FILE_TYPE = "xsd";
export const SVG_FILE_TYPE = "svg";
export const GIF_FILE_TYPE = "gif";
export const ZIP_FILE_TYPE = "zip";

export const CSV_MIME_TYPE = "text/csv";
export const PDF_MIME_TYPE = "application/pdf";
export const ZIP_MIME_TYPE = "application/zip";
export const X_ZIP_COMPRESSED_MIME_TYPE = "application/x-zip-compressed";
export const OCTET_STREAM_MIME_TYPE = "application/octet-stream";
export const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const XLSM_MIME_TYPE = "application/vnd.ms-excel.sheet.macroenabled.12";

const ALLOWED_DOCS = [
  {
    mimeType: PDF_MIME_TYPE,
    fileType: PDF_FILE_TYPE,
  },
  {
    mimeType: DOCX_MIME_TYPE,
    fileType: DOCX_FILE_TYPE,
  },
  {
    mimeType: XLSX_MIME_TYPE,
    fileType: XLSX_FILE_TYPE,
  },
  {
    mimeType: XLSM_MIME_TYPE,
    fileType: XLSM_FILE_TYPE,
  },
] as const;

export const ALLOWED_IMPORT_MIME_TYPES = ALLOWED_DOCS.map(
  (doc) => doc.mimeType,
);

export const ALLOWED_ECTD_MIME_TYPES = [
  ZIP_MIME_TYPE,
  X_ZIP_COMPRESSED_MIME_TYPE,
] as const;

export const ALLOWED_PROJECT_PARTICIPANTS_MIME_TYPES = [
  XLSX_MIME_TYPE,
] as const;

export const DOCUMENT_TYPES_FOR_PREVIEW = [
  DOC_FILE_TYPE,
  DOCX_FILE_TYPE,
  PDF_FILE_TYPE,
] as const;

export const DOCUMENT_TYPES_FOR_DOWNLOAD = [
  DOC_FILE_TYPE,
  DOCX_FILE_TYPE,
  PDF_FILE_TYPE,
  ZIP_FILE_TYPE,
] as const;

export const ALLOWED_GENERAL_DOC_MIME_TYPES = [
  DOCX_MIME_TYPE,
  PDF_MIME_TYPE,
] as const;

const EXCLUDED_SUBTYPES = [
  DocumentSubtype.Spreadsheet,
  DocumentSubtype.Question,
  DocumentSubtype.QuestionResponse,
];

export const SUPPORTED_SUBTYPES = Object.values(DocumentSubtype).filter(
  (subtype) => !EXCLUDED_SUBTYPES.includes(subtype),
);

// TODO: (AR): Later when we start supporting HAQs for Structured Documents we'll remove TEMP_SUPPORTED_SUBTYPES
// and will use SUPPORTED_SUBTYPES again.
export const TEMP_SUPPORTED_SUBTYPES = without(
  SUPPORTED_SUBTYPES,
  DocumentSubtype.StructuredDocument,
);

export const SUPPORTED_ECTD_VERSION = "3.2.2";

export const DOCUMENT_READER_TYPES = {
  ECTD: [DocumentSubtype.ECTD],
  PDF: Object.values(DocumentSubtype).filter(
    (type) => type !== DocumentSubtype.ECTD,
  ) as DocumentSubtype[],
};

export enum BulkAssetsActionOperation {
  LOCK = "LOCK",
  UNLOCK = "UNLOCK",
}
